import type { DateValue, Key } from "react-aria-components";

import { today } from "@internationalized/date";
import {
  createEasternDateString,
  type GetRequestsListItem,
  RequestStatusEnum,
  Timezone,
  toEasternCalendarDate,
} from "@nlc/shared-types";
import { useState } from "react";
import {
  DateField,
  DateInput,
  DateSegment,
  DialogTrigger,
  Group,
} from "react-aria-components";

import { Avatar } from "@/components/avatar";
import { Badge } from "@/components/badge";
import { Button } from "@/components/button";
import { Calendar } from "@/components/calendar/Calendar.tsx";
import { XIcon } from "@/components/icons.tsx";
import { Popover } from "@/components/popover";
import { SearchField, SearchInput } from "@/components/search-field";
import {
  Select,
  SelectListBox,
  SelectListItem,
  SelectPopover,
} from "@/components/select";
import { COMPLEXITY_OPTIONS } from "@/features/shared/constants/complexityConstants";
import { STATUS_OPTIONS } from "@/features/shared/constants/statusColors";
import { useDebounce } from "@/hooks/useDebounce";
import { convertUTCtoEastern } from "@/utils/timezoneConverter";

import { useAssigneeOptions } from "../../hooks/useAssigneeOptions";
import { useBrandOptions } from "../../hooks/useBrandOptions";
import { useUpdateRequestField } from "../../hooks/useUpdateRequestField";
import { dueDateCellWithInternal, statusCell } from "./commonCells";

// Type alias for inline edit data
type RequestRowData = GetRequestsListItem;

// Shared utility functions for inline editors
const createEscapeKeyHandler =
  (setIsOpen: (open: boolean) => void) => (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
    }
  };

const createOpenChangeHandler =
  (
    updateMutation: { isPending: boolean },
    setIsOpen: (open: boolean) => void,
  ) =>
  (open: boolean) => {
    if (!updateMutation.isPending) {
      setIsOpen(open);
    }
  };

/**
 * Floating status editor with immediate dropdown activation
 * Shows dropdown immediately on click, no manual action buttons needed
 */
export function InlineStatusCell({
  data,
  value,
}: {
  data: RequestRowData;
  value: unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const updateMutation = useUpdateRequestField();

  const statusValue =
    value && typeof value === "object" && "id" in value ? value.id : null;

  const selectableStatusOptions = STATUS_OPTIONS.filter(
    (option) => option.id !== RequestStatusEnum.Completed,
  );

  const handleSelectionChange = async (selectedKey: Key | null) => {
    if (selectedKey === null) return;
    if (selectedKey !== undefined && selectedKey !== statusValue) {
      try {
        await updateMutation.mutateAsync({
          field: "statusId",
          requestId: data.id,
          value: Number(selectedKey),
        });
        setIsOpen(false);

        // Show success feedback briefly
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } catch {
        // Error is handled by the mutation - close dropdown
        setIsOpen(false);
      }
    } else {
      // Same value selected, just close
      setIsOpen(false);
    }
  };

  const handleKeyDown = createEscapeKeyHandler(setIsOpen);

  const handleOpenChange = createOpenChangeHandler(updateMutation, setIsOpen);

  // Completed requests can only leave that status via the "Reopen" action,
  // so the status tag is read-only here instead of an editable dropdown.
  if (statusValue === RequestStatusEnum.Completed) {
    return <div>{statusCell(value)}</div>;
  }

  // If no status, render as plain text to avoid any badge rendering issues
  if (!statusValue) {
    return (
      <div onKeyDown={handleKeyDown}>
        <Select
          aria-label="Select status"
          isDisabled={updateMutation.isPending}
          isOpen={isOpen}
          onOpenChange={handleOpenChange}
          onSelectionChange={handleSelectionChange}
          selectedKey={null}
        >
          <Button
            className={`flex items-center justify-start outline-none cursor-pointer ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
            variant="unstyled"
          >
            {updateMutation.isPending ? (
              <div className="flex items-center justify-start gap-1">
                <span className="text-text-tertiary">--</span>
                <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : (
              <span className="text-text-tertiary">--</span>
            )}
          </Button>
          <SelectPopover className="w-fit" placement="bottom start">
            <SelectListBox>
              {selectableStatusOptions.map(({ id, label, variant }) => (
                <SelectListItem id={id} key={id} textValue={label}>
                  <Badge isRounded size="small" variant={variant}>
                    {label}
                  </Badge>
                </SelectListItem>
              ))}
            </SelectListBox>
          </SelectPopover>
        </Select>
      </div>
    );
  }

  // For existing status, use the full component with statusCell rendering
  return (
    <div onKeyDown={handleKeyDown}>
      <Select
        aria-label="Select status"
        isDisabled={updateMutation.isPending}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        onSelectionChange={handleSelectionChange}
        selectedKey={statusValue.toString()}
      >
        <Button
          className={`flex items-center justify-start outline-none cursor-pointer ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
          variant="unstyled"
        >
          {updateMutation.isPending ? (
            <div className="flex items-center justify-start gap-1">
              {statusCell(value)}
              <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            statusCell(value)
          )}
        </Button>
        <SelectPopover className="w-fit" placement="bottom start">
          <SelectListBox>
            {selectableStatusOptions.map(({ id, label, variant }) => (
              <SelectListItem id={id} key={id} textValue={label}>
                <Badge isRounded size="small" variant={variant}>
                  {label}
                </Badge>
              </SelectListItem>
            ))}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </div>
  );
}

/**
 * Floating complexity editor with immediate dropdown activation
 * Shows dropdown immediately on click, no manual action buttons needed
 */
export function InlineComplexityCell({
  data,
  value,
}: {
  data: RequestRowData;
  value: unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const updateMutation = useUpdateRequestField();

  // Use centralized complexity options mapped to string values for the inline editor
  const complexityOptionsForEditor = COMPLEXITY_OPTIONS.map((option) => ({
    id: option.stringValue,
    label: option.name,
    variant: option.variant,
  }));

  const complexityValue = (value as string) || "";
  const currentOption = complexityOptionsForEditor.find(
    (opt) => opt.id === complexityValue,
  );

  const handleSelectionChange = async (selectedKey: Key | null) => {
    if (selectedKey && selectedKey !== complexityValue) {
      try {
        await updateMutation.mutateAsync({
          field: "complexity",
          requestId: data.id,
          value: selectedKey,
        });
        setIsOpen(false);

        // Show success feedback briefly
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } catch {
        // Error is handled by the mutation - close dropdown
        setIsOpen(false);
      }
    } else {
      // Same value selected, just close
      setIsOpen(false);
    }
  };

  const handleKeyDown = createEscapeKeyHandler(setIsOpen);

  const handleOpenChange = createOpenChangeHandler(updateMutation, setIsOpen);

  return (
    <div onKeyDown={handleKeyDown}>
      <Select
        aria-label="Select complexity"
        isDisabled={updateMutation.isPending}
        isOpen={isOpen}
        onOpenChange={handleOpenChange}
        onSelectionChange={handleSelectionChange}
        selectedKey={complexityValue}
      >
        <Button
          className={`flex items-center justify-start outline-none cursor-pointer ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
          variant="unstyled"
        >
          <div className="line-clamp-3 break-words">
            {updateMutation.isPending ? (
              <div className="flex items-center justify-start gap-1">
                <Badge
                  isRounded
                  size="small"
                  variant={currentOption?.variant ?? "primary"}
                >
                  {currentOption ? currentOption.label : "--"}
                </Badge>
                <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
              </div>
            ) : currentOption ? (
              <Badge isRounded size="small" variant={currentOption.variant}>
                {currentOption.label}
              </Badge>
            ) : (
              <span className="text-sm text-text-tertiary">--</span>
            )}
          </div>
        </Button>
        <SelectPopover className="w-fit" placement="bottom start">
          <SelectListBox>
            {complexityOptionsForEditor.map(({ id, label, variant }) => (
              <SelectListItem id={id} key={id} textValue={label}>
                <Badge isRounded size="small" variant={variant}>
                  {label}
                </Badge>
              </SelectListItem>
            ))}
          </SelectListBox>
        </SelectPopover>
      </Select>
    </div>
  );
}

/**
 * Floating assignee editor with search and dropdown in popover
 * Shows popover immediately on click, no manual action buttons needed
 */
export function InlineAssigneeCell({
  data,
  value,
}: {
  data: RequestRowData;
  value: unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState<null | string>(null);
  const [searchValue, setSearchValue] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const updateMutation = useUpdateRequestField();

  // Debounce search to prevent excessive API calls
  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch assignee options from API with search - only when open
  const { data: assigneeData, isLoading } = useAssigneeOptions(
    data.id,
    debouncedSearch || undefined,
    isOpen,
  );

  const assigneeOptions = assigneeData?.assignees || [];

  const assigneeValue =
    value && typeof value === "object" && "id" in value ? value.id : null;

  const handleSelectionChange = async (selectedKey: Key | null) => {
    // Validate: complexity must be selected first
    if (selectedKey && !data.complexity) {
      setError("Please select a complexity level before assigning.");

      return;
    }

    if (selectedKey && selectedKey !== assigneeValue) {
      try {
        await updateMutation.mutateAsync({
          field: "assigneeId",
          requestId: data.id,
          value: Number(selectedKey),
        });
        setIsOpen(false);
        setError(null);
        setSearchValue("");

        // Show success feedback briefly
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } catch {
        // Error is handled by the mutation - close popover
        setIsOpen(false);
        setError(null);
        setSearchValue("");
      }
    } else {
      // Same value selected or cancel, just close
      setIsOpen(false);
      setError(null);
      setSearchValue("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      setError(null);
      setSearchValue("");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!updateMutation.isPending) {
      setIsOpen(open);
      if (!open) {
        setError(null);
        setSearchValue("");
      }
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        <Button
          className={`text-left cursor-pointer outline-none rounded px-1 py-1 ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
          variant="unstyled"
        >
          <div className="line-clamp-3 break-words">
            {updateMutation.isPending ? (
              <div className="flex items-center gap-1">
                {value && typeof value === "object" && "name" in value ? (
                  <div>
                    <span className="text-sm font-medium text-text-primary">
                      {(value as { name: string }).name}
                    </span>
                    {(value as { designation?: string }).designation && (
                      <span className="text-xs text-text-tertiary ml-2">
                        {(value as { designation?: string }).designation}
                      </span>
                    )}
                  </div>
                ) : (
                  <span className="text-text-tertiary">--</span>
                )}
                <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin ml-1" />
              </div>
            ) : (
              <div>
                {value && typeof value === "object" && "name" in value ? (
                  <>
                    <span className="text-sm font-medium text-text-primary">
                      {(value as { name: string }).name}
                    </span>
                  </>
                ) : (
                  <span className="text-sm font-medium text-text-primary">
                    --
                  </span>
                )}
              </div>
            )}
          </div>
        </Button>

        <Popover className="px-4 py-3 min-w-64" placement="bottom start">
          {/* Search input */}
          <div className="mb-3">
            <SearchField aria-label="Search assignees" className="w-full">
              <SearchInput
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder="Search..."
                value={searchValue}
              />
            </SearchField>
          </div>

          {/* Assignee selection */}
          <Select
            aria-label="Select assignee"
            className="w-full"
            isDisabled={
              updateMutation.isPending || !data.complexity || isLoading
            }
            isOpen={true}
            onSelectionChange={handleSelectionChange}
            selectedKey={assigneeValue?.toString() ?? ""}
          >
            <SelectListBox className="max-h-48 overflow-y-auto">
              {/* Loading state */}
              {isLoading && (
                <SelectListItem
                  id="loading"
                  isDisabled
                  key="loading"
                  textValue="Loading..."
                >
                  <span className="text-sm text-gray-500 italic">
                    Loading...
                  </span>
                </SelectListItem>
              )}

              {/* Assignee options */}
              {assigneeOptions.map((assignee) => (
                <SelectListItem
                  id={assignee.id}
                  key={assignee.id}
                  textValue={assignee.name}
                >
                  <div className="flex flex-col">
                    <span className="text-sm font-medium text-text-primary">
                      {assignee.name}
                    </span>
                    {assignee.designation && (
                      <span className="text-xs text-gray-500">
                        {assignee.designation}
                      </span>
                    )}
                  </div>
                </SelectListItem>
              ))}

              {/* No results message */}
              {!isLoading &&
                assigneeOptions.length === 0 &&
                debouncedSearch && (
                  <SelectListItem
                    id="no-results"
                    isDisabled
                    key="no-results"
                    textValue="No results"
                  >
                    <span className="text-gray-500 italic">
                      No assignees found
                    </span>
                  </SelectListItem>
                )}
            </SelectListBox>
          </Select>

          {/* Error message */}
          {error && (
            <div className="mt-2">
              <span className="text-xs text-red-600">{error}</span>
            </div>
          )}
        </Popover>
      </DialogTrigger>
    </div>
  );
}

/**
 * Floating date editor with beautiful Calendar component
 * Shows calendar immediately on click, no manual action buttons needed
 */
export function InlineDueDateCell({
  data,
  value,
}: {
  data: RequestRowData;
  value: unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [justSaved, setJustSaved] = useState(false);
  const [focusedValue, setFocusedValue] = useState<DateValue | null>(null);
  const [optimisticDateValue, setOptimisticDateValue] =
    useState<DateValue | null>(null);
  const [lockedDisplayValue, setLockedDisplayValue] = useState<null | string>(
    null,
  );
  const updateMutation = useUpdateRequestField();

  // Determine which date to edit: internal due date takes precedence
  // internalDueDate takes priority, falls back to customerDueDate, then dueDate
  const rawInternal = data.internalDueDate;
  const rawDue = data.customerDueDate ?? data.dueDate;
  const currentDate = rawInternal || rawDue || value;
  const isEditingInternal = !!rawInternal;

  // Convert UTC date string to DateValue for Calendar component
  // Converts UTC to Eastern timezone before creating CalendarDate
  const stringToDateValue = (dateString: unknown): DateValue | null => {
    if (!dateString || typeof dateString !== "string") return null;
    try {
      const easternDateString = convertUTCtoEastern(dateString, "YYYY-MM-DD");

      if (!easternDateString) return null;

      // Convert Eastern YYYY-MM-DD to CalendarDate for date picker
      return toEasternCalendarDate(easternDateString);
    } catch {
      return null;
    }
  };

  // Convert DateValue back to Eastern timezone date string for API
  // This preserves the intended Eastern date without timezone conversion issues
  const dateValueToString = (dateValue: DateValue | null): string => {
    if (!dateValue) return "";
    try {
      if ("year" in dateValue && "month" in dateValue && "day" in dateValue) {
        // Use timezone-aware utility to preserve Eastern timezone intent
        return createEasternDateString(
          dateValue.year,
          dateValue.month,
          dateValue.day,
        );
      }

      return "";
    } catch {
      return "";
    }
  };

  const currentDateValue = stringToDateValue(currentDate);

  const handleDateChange = async (newDateValue: DateValue | null) => {
    const dateString = dateValueToString(newDateValue);
    const apiField = isEditingInternal ? "internalDueDate" : "dueDate";

    const lockedDateUTC = dateString
      ? new Date(`${dateString}T00:00:00-05:00`).toISOString()
      : null;

    setLockedDisplayValue(lockedDateUTC);

    setOptimisticDateValue(newDateValue);

    try {
      await updateMutation.mutateAsync({
        field: apiField,
        requestId: data.id,
        value: dateString,
      });
      setIsOpen(false);

      // Show success feedback briefly
      setJustSaved(true);
      setTimeout(() => {
        setJustSaved(false);
      }, 2000);

      setTimeout(() => {
        setOptimisticDateValue(null);
        setLockedDisplayValue(null);
      }, 3000);
    } catch {
      // Error: clear all locks immediately
      setIsOpen(false);
      setOptimisticDateValue(null);
      setLockedDisplayValue(null);
    }
  };

  const handleTodayClick = () => {
    const todayDate = today(Timezone.EST);

    handleDateChange(todayDate);
  };

  const handleClearClick = () => {
    handleDateChange(null);
  };

  const handleKeyDown = createEscapeKeyHandler(setIsOpen);

  const handleOpenChange = (open: boolean) => {
    if (!updateMutation.isPending) {
      setIsOpen(open);
      if (open) {
        // Set focused value to current date, or today if no date exists
        // This aligns with React Aria's default behavior and provides clear UX
        setFocusedValue(currentDateValue || today(Timezone.EST));
      }
    }
  };

  const renderDateDisplay = () => {
    if (lockedDisplayValue) {
      return dueDateCellWithInternal(lockedDisplayValue, {
        ...data,
        [isEditingInternal ? "dueDate" : "customerDueDate"]: lockedDisplayValue,
        [isEditingInternal ? "internalDueDate" : "dueDate"]: lockedDisplayValue,
      });
    }

    if (optimisticDateValue) {
      const optimisticDateString = dateValueToString(optimisticDateValue);
      const optimisticDateUTC = optimisticDateString
        ? new Date(`${optimisticDateString}T00:00:00-05:00`).toISOString()
        : optimisticDateString;

      return dueDateCellWithInternal(optimisticDateUTC, {
        ...data,
        [isEditingInternal ? "dueDate" : "customerDueDate"]: optimisticDateUTC,
        [isEditingInternal ? "internalDueDate" : "dueDate"]: optimisticDateUTC,
      });
    }

    return dueDateCellWithInternal(value, data);
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        <Button
          className={`text-left cursor-pointer outline-none rounded px-1 py-1 ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
          variant="unstyled"
        >
          {updateMutation.isPending ? (
            <div className="flex items-center gap-1">
              {renderDateDisplay()}
              <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : (
            renderDateDisplay()
          )}
        </Button>

        <Popover className="px-5 py-4" placement="bottom start">
          <div className="mb-4 flex gap-2">
            <DateField
              aria-label="Select date"
              className="flex grow justify-between rounded-md border border-border-primary bg-bg-primary px-[14px] py-md text-md text-text-primary"
              onChange={(newDate) => {
                setFocusedValue(newDate);
                if (newDate) {
                  handleDateChange(newDate);
                }
              }}
              value={currentDateValue}
            >
              <Group>
                <DateInput className="flex">
                  {(segment) => (
                    <DateSegment className="outline-none" segment={segment} />
                  )}
                </DateInput>
              </Group>
              <Button
                className="flex size-6 justify-center self-end rounded-full hover:bg-bg-tertiary"
                onPress={handleClearClick}
                variant="unstyled"
              >
                <XIcon className="m-auto size-4" />
              </Button>
            </DateField>

            <Button color="gray" onPress={handleTodayClick} variant="secondary">
              Today
            </Button>
          </div>
          <Calendar
            aria-label="Select date"
            focusedValue={focusedValue}
            onChange={handleDateChange}
            onFocusChange={setFocusedValue}
            timezone={Timezone.EST}
            value={currentDateValue}
          />
        </Popover>
      </DialogTrigger>
    </div>
  );
}

/**
 * Floating brand editor with search and dropdown in popover
 * Shows popover immediately on click, no manual action buttons needed
 */
export function InlineBrandCell({
  data,
  value,
}: {
  data: RequestRowData;
  value: unknown;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [justSaved, setJustSaved] = useState(false);
  const updateMutation = useUpdateRequestField();

  // Debounce search to prevent excessive API calls
  const debouncedSearch = useDebounce(searchValue, 300);

  // Fetch brand options from API with search - only when open
  const { data: brandData, isLoading } = useBrandOptions(
    data.id,
    debouncedSearch || undefined,
    isOpen,
  );

  const brandOptions = brandData?.brands || [];

  const brandValue =
    value && typeof value === "object" && "id" in value ? value.id : null;

  const handleSelectionChange = async (selectedKey: Key | null) => {
    if (selectedKey && selectedKey !== brandValue) {
      try {
        await updateMutation.mutateAsync({
          field: "brandId",
          requestId: data.id,
          value: Number(selectedKey),
        });
        setIsOpen(false);
        setSearchValue("");

        // Show success feedback briefly
        setJustSaved(true);
        setTimeout(() => setJustSaved(false), 2000);
      } catch {
        // Error is handled by the mutation - close popover
        setIsOpen(false);
        setSearchValue("");
      }
    } else {
      // Same value selected or cancel, just close
      setIsOpen(false);
      setSearchValue("");
    }
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(e.target.value);
  };

  const handleSearchClear = () => {
    setSearchValue("");
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape") {
      setIsOpen(false);
      setSearchValue("");
    }
  };

  const handleOpenChange = (open: boolean) => {
    if (!updateMutation.isPending) {
      setIsOpen(open);
      if (!open) {
        setSearchValue("");
      }
    }
  };

  return (
    <div onKeyDown={handleKeyDown}>
      <DialogTrigger isOpen={isOpen} onOpenChange={handleOpenChange}>
        <Button
          className={`text-left cursor-pointer outline-none rounded px-1 py-1 ${updateMutation.isPending ? "opacity-75" : ""} ${justSaved ? "ring-2 ring-green-200 rounded" : ""}`}
          variant="unstyled"
        >
          <div className="line-clamp-3 break-words min-w-0">
            {updateMutation.isPending ? (
              <div className="flex items-center gap-1 min-w-0">
                {value && typeof value === "object" && "name" in value ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar
                      alt={(value as { name: string }).name}
                      className="size-5 rounded-full border border-border-secondary object-cover shrink-0"
                      src={
                        (value as { profilePicture?: string }).profilePicture
                      }
                    />
                    <span className="text-sm font-medium text-text-primary min-w-0 line-clamp-3 break-all">
                      {(value as { name: string }).name}
                    </span>
                  </div>
                ) : (
                  <span className="text-text-tertiary">--</span>
                )}
                <div className="w-3 h-3 border border-gray-300 border-t-transparent rounded-full animate-spin ml-1 shrink-0" />
              </div>
            ) : (
              <div className="min-w-0">
                {value && typeof value === "object" && "name" in value ? (
                  <div className="flex items-center gap-2 min-w-0">
                    <Avatar
                      alt={(value as { name: string }).name}
                      className="size-5 rounded-full border border-border-secondary object-cover shrink-0"
                      src={
                        (value as { profilePicture?: string }).profilePicture
                      }
                    />
                    <span className="text-sm font-medium text-text-primary min-w-0 line-clamp-3 break-all">
                      {(value as { name: string }).name}
                    </span>
                  </div>
                ) : (
                  <span className="text-sm font-medium text-text-primary">
                    --
                  </span>
                )}
              </div>
            )}
          </div>
        </Button>

        <Popover className="px-4 py-3 min-w-64" placement="bottom start">
          {/* Search input */}
          <div className="mb-3">
            <SearchField aria-label="Search brands" className="w-full">
              <SearchInput
                onChange={handleSearchChange}
                onClear={handleSearchClear}
                placeholder="Search brands..."
                value={searchValue}
              />
            </SearchField>
          </div>

          {/* Brand selection */}
          <Select
            aria-label="Select brand"
            className="w-full"
            isDisabled={updateMutation.isPending || isLoading}
            isOpen={true}
            onSelectionChange={handleSelectionChange}
            selectedKey={brandValue?.toString() ?? ""}
          >
            <SelectListBox className="max-h-48 overflow-y-auto">
              {/* No brand option */}
              <SelectListItem id={0} key={0} textValue="No brand">
                <span className="text-sm text-gray-500">No brand</span>
              </SelectListItem>

              {/* Loading state */}
              {isLoading && (
                <SelectListItem
                  id="loading"
                  isDisabled
                  key="loading"
                  textValue="Loading..."
                >
                  <span className="text-sm text-gray-500 italic">
                    Loading...
                  </span>
                </SelectListItem>
              )}

              {/* Brand options */}
              {brandOptions.map((brand) => (
                <SelectListItem
                  id={brand.id}
                  key={brand.id}
                  textValue={brand.name}
                >
                  <div className="flex items-center gap-2">
                    <Avatar
                      alt={brand.name}
                      className="size-6 rounded-full border border-border-secondary object-cover shrink-0"
                      src={brand.profilePictureUrl}
                    />
                    <span className="text-sm font-medium text-text-primary">
                      {brand.name}
                    </span>
                  </div>
                </SelectListItem>
              ))}

              {/* No results message */}
              {!isLoading && brandOptions.length === 0 && debouncedSearch && (
                <SelectListItem
                  id="no-results"
                  isDisabled
                  key="no-results"
                  textValue="No results"
                >
                  <span className="text-gray-500 italic">No brands found</span>
                </SelectListItem>
              )}
            </SelectListBox>
          </Select>
        </Popover>
      </DialogTrigger>
    </div>
  );
}
